import { useState, useEffect } from "react";
import "@/App.css"; 
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";

// Scroll to top component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

import { 
  Map, 
  Scan, 
  Mountain, 
  Wheat,
  Factory,
  HardHat,
  Phone,
  Mail,
  MapPin,
  Instagram,
  ChevronDown,
  Menu,
  X,
  ArrowRight,
  CheckCircle2,
  Send,
  Crosshair,
  TreePine,
  Zap,
  Building2,
  Camera,
  Plane
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";

// Services Data
const servicesData = [
  {
    id: 'topographical-survey',
    icon: Map,
    title: 'Topographical Survey',
    image: '/images/services/topographical.jpg',
    shortDesc: 'High-precision terrain mapping with centimeter-level accuracy.',
    fullDesc: 'Our topographical survey services utilize advanced drone technology to create detailed terrain maps with centimeter-level accuracy. Perfect for construction, mining, and land development projects.',
    features: [
      'Centimeter-level accuracy (5cm)',
      'Large area coverage in minimal time',
      'Digital Terrain Models (DTM)',
      'Digital Surface Models (DSM)',
      'Contour mapping',
      'Volume calculations',
      'Cross-section profiles',
      'CAD-ready deliverables'
    ],
    applications: ['Construction Planning', 'Land Development', 'Mining Operations', 'Infrastructure Projects', 'Urban Planning']
  },
  {
    id: 'drone-inspection',
    icon: Scan,
    title: 'Drone Inspection',
    image: '/images/services/droninspection.jpg',
    shortDesc: 'Visual and thermal inspections for infrastructure and assets.',
    fullDesc: 'Professional drone inspection services for infrastructure, power lines, buildings, and industrial assets. We provide detailed visual and thermal analysis to identify issues before they become problems.',
    features: [
      'Visual inspection with 4K cameras',
      'Thermal imaging for heat detection',
      'Detailed defect identification',
      'Infrastructure condition assessment',
      'Safety hazard detection',
      'Comprehensive inspection reports',
      'Before/after comparisons',
      'Maintenance recommendations'
    ],
    applications: ['Power Lines', 'Telecom Towers', 'Buildings & Facades', 'Solar Panels', 'Wind Turbines', 'Bridges & Structures']
  },
  {
    id: '3d-mapping',
    icon: Mountain,
    title: '3D Mapping',
    image: '/images/services/mapping.jpg',
    shortDesc: 'Detailed 3D models and orthomosaics for planning.',
    fullDesc: 'Create stunning 3D models and accurate orthomosaics of any site. Our 3D mapping services provide invaluable data for planning, visualization, and analysis.',
    features: [
      'Photorealistic 3D models',
      'High-resolution orthomosaics',
      'Point cloud generation',
      'Textured mesh models',
      'Virtual site tours',
      'Measurement tools',
      'Export to multiple formats',
      'Web-based viewing'
    ],
    applications: ['Real Estate Marketing', 'Heritage Documentation', 'Construction Progress', 'Mining Pit Models', 'Urban Development']
  },
  {
    id: 'agriculture-analysis',
    icon: Wheat,
    title: 'Agriculture Analysis',
    image: '/images/services/agriculture.jpg',
    shortDesc: 'Crop health monitoring and precision farming solutions.',
    fullDesc: 'Transform your farming operations with our drone-based agriculture analysis. Monitor crop health, optimize irrigation, and increase yields with precision farming technology.',
    features: [
      'NDVI crop health mapping',
      'Plant counting and spacing',
      'Irrigation management',
      'Pest and disease detection',
      'Yield estimation',
      'Soil analysis support',
      'Variable rate application maps',
      'Seasonal monitoring'
    ],
    applications: ['Crop Monitoring', 'Precision Farming', 'Irrigation Planning', 'Pest Management', 'Yield Optimization']
  },
  {
    id: 'mining-surveys',
    icon: Factory,
    title: 'Mining Surveys',
    image: '/images/services/mining.jpg',
    shortDesc: 'Stockpile volumetrics and pit surveys for mining operations.',
    fullDesc: 'Comprehensive drone survey solutions for mining operations. From stockpile measurements to pit surveys, we provide accurate data for efficient mine management.',
    features: [
      'Stockpile volume calculations',
      'Pit and quarry surveys',
      'Haul road inspections',
      'Slope stability monitoring',
      'Progress tracking',
      'Blast planning support',
      'Regulatory compliance',
      'Safety surveillance'
    ],
    applications: ['Coal Mines', 'Quarries', 'Open Pit Mines', 'Stockyard Management', 'Mine Planning']
  }
];

// Navigation Component
const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/#about', label: 'About' },
    { href: '/#services', label: 'Services' },
    { href: '/#process', label: 'Process' },
    { href: '/#contact', label: 'Contact' },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src='/images/logo.png'
              alt="Droniq Logo"
              className="w-10 h-10 object-contain"
            />
            <div>
              <span className="font-heading font-bold text-xl text-slate-900 tracking-tight">DRONIQ</span>
              <span className="block text-[10px] font-medium text-slate-500 tracking-widest">GEO SURVEYS</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.href.startsWith('/#') ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="nav-link text-sm font-medium text-slate-600 hover:text-cyan-500"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="nav-link text-sm font-medium text-slate-600 hover:text-cyan-500"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              )
            ))}
            <Link to="/get-quote">
              <Button 
                className="btn-primary text-white font-medium text-sm px-6 py-5 rounded-full"
                data-testid="nav-get-quote-btn"
              >
                Get Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-slate-700 p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu md:hidden fixed top-20 right-0 w-full h-screen bg-white ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="flex flex-col items-center gap-6 pt-8 px-6">
          {navLinks.map((link) => (
            link.href.startsWith('/#') ? (
              <a
                key={link.href}
                href={link.href}
                className="text-lg font-medium text-slate-700 hover:text-cyan-500"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                className="text-lg font-medium text-slate-700 hover:text-cyan-500"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            )
          ))}
          <Link to="/get-quote" onClick={() => setIsMobileMenuOpen(false)}>
            <Button className="btn-primary text-white font-medium text-sm px-8 py-6 rounded-full mt-4">
              Get Quote
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => (
  <section id="home" className="hero-bg min-h-screen flex items-center pt-20" data-testid="hero-section">
    <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-cyan-500 font-semibold text-sm tracking-wide uppercase mb-4" data-testid="hero-tagline">
            Professional Drone Services
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
            Drone Survey &<br />
            <span className="text-gradient">Mapping Solutions</span>
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
            Transform your projects with precision drone technology. We deliver accurate 
            topographical surveys, 3D mapping, and aerial inspections across India.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="#services">
              <Button 
                className="btn-primary text-white font-medium px-8 py-6 rounded-full text-base"
                data-testid="hero-explore-services-btn"
              >
                Explore Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <a href="#contact">
              <Button 
                variant="outline"
                className="border-2 border-slate-300 hover:border-cyan-500 text-slate-700 hover:text-cyan-500 font-medium px-8 py-6 rounded-full text-base bg-white"
                data-testid="hero-contact-btn"
              >
                Contact Us
              </Button>
            </a>
          </div>
        </div>
        
        {/* Hero Image */}
        <div className="relative">
          <div className="aspect-square max-w-md mx-auto rounded-3xl overflow-hidden shadow-2xl">
            <img 
              src="/images/hero/hero-drone.jpeg"
              alt="Professional drone for aerial surveying"
              className="w-full h-full object-cover"
            />
          </div>
          {/* Floating Stats */}
          <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg p-4 border border-slate-100">
            <div className="text-2xl font-bold text-cyan-500">142+</div>
            <div className="text-xs text-slate-500">Projects Done</div>
          </div>
          <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-lg p-4 border border-slate-100">
            <div className="text-2xl font-bold text-teal-500">5cm</div>
            <div className="text-xs text-slate-500">Accuracy</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Stats Section
const StatsSection = () => {
  const stats = [
    { value: '142+', label: 'Projects Completed' },
    { value: '5cm', label: 'Survey Accuracy' },
    { value: '50+', label: 'Cities Covered' },
    { value: '100%', label: 'Client Satisfaction' },
  ];

  return (
    <section className="py-16 bg-white border-y border-slate-100" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="stat-item text-center pt-8" data-testid={`stat-${index}`}>
              <div className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-slate-500 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => (
  <section id="about" className="section-padding bg-slate-50" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-2 gap-16 items-center">
        {/* About Image */}
        <div className="relative">
          <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl">
            <img 
              src="/images/about/team-drone.jpeg"
              alt="Drone operator conducting aerial survey"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -right-6 bg-cyan-500 text-white rounded-2xl p-6 shadow-xl">
            <div className="text-4xl font-bold">7+</div>
            <div className="text-sm opacity-90">Years Experience</div>
          </div>
        </div>

        <div>
          <p className="text-cyan-500 font-semibold text-sm tracking-wide uppercase mb-4">
            About Droniq
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Hassle-Free Drone<br />Survey Services
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed mb-6">
            Droniq Geo Surveys is a leading drone survey and mapping company based in Ahmedabad, 
            India. We specialize in transforming aerial data into actionable insights using 
            cutting-edge UAV technology.
          </p>
          <p className="text-slate-600 leading-relaxed mb-8">
            Our services span across mining, agriculture, construction, infrastructure, 
            and urban planning sectors. We automate manual processes to make your operations 
            efficient, expedient, and economical.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {[
              'DGCA Certified Pilots',
              'High-Precision Equipment',
              'Fast Turnaround',
              'Detailed Analytics'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-teal-500 flex-shrink-0" />
                <span className="text-slate-700 text-sm font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section
const ServicesSection = () => (
  <section id="services" className="section-padding bg-white" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <p className="text-cyan-500 font-semibold text-sm tracking-wide uppercase mb-4">
          Our Services
        </p>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          Comprehensive Drone Solutions
        </h2>
        <p className="text-slate-600 text-lg">
          From aerial surveys to detailed inspections, we provide end-to-end drone 
          services tailored to your industry requirements.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {servicesData.map((service, index) => (
          <Link
            key={service.id}
            to={`/services/${service.id}`}
            className="service-card bg-white border border-slate-200 rounded-2xl p-8 block"
            data-testid={`service-card-${index}`}
          >
            <div className="w-14 h-14 bg-cyan-50 rounded-xl flex items-center justify-center mb-6">
              <service.icon className="w-7 h-7 text-cyan-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
              {service.title}
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {service.shortDesc}
            </p>
            <span className="inline-flex items-center text-cyan-500 font-medium text-sm">
              Learn More <ArrowRight className="w-4 h-4 ml-1" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

// Process Section
const ProcessSection = () => {
  const steps = [
    { number: '01', title: 'Consultation', desc: 'Discuss your project requirements' },
    { number: '02', title: 'Planning', desc: 'Plan optimal flight path' },
    { number: '03', title: 'Data Capture', desc: 'Execute survey with precision' },
    { number: '04', title: 'Processing', desc: 'Advanced data processing' },
    { number: '05', title: 'Delivery', desc: 'Receive detailed reports' }
  ];

  return (
    <section id="process" className="section-padding bg-slate-50" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-cyan-500 font-semibold text-sm tracking-wide uppercase mb-4">
            How We Work
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Our Simple Process
          </h2>
          <p className="text-slate-600 text-lg">
            A streamlined workflow designed to deliver accurate results on time.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="process-step text-center" data-testid={`process-step-${index}`}>
              <div className="w-20 h-20 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-4 border-4 border-cyan-100">
                <span className="text-2xl font-bold text-cyan-500">{step.number}</span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                {step.title}
              </h3>
              <p className="text-slate-500 text-sm">
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// UPGRADED Contact Section (Clickable Cards + WhatsApp Form Dispatcher)
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedService = servicesData.find(s => s.id === formData.service);
    const serviceName = selectedService ? selectedService.title : (formData.service === 'other' ? 'Other / Custom' : 'General Inquiry');

    // Using strictly escaped Unicode. Editor encoding cannot break this:
    const textPayload = 
`*New Website Inquiry - Droniq Geo Surveys* \u{1F6F8}

*\u{1F464} Name:* ${formData.name}
*\u{1F4E7} Email:* ${formData.email}
*\u{1F4DE} Phone:* ${formData.phone || "Not provided"}
*\u{1F3AF} Service:* ${serviceName}

*\u{1F4DD} Project Details:*
${formData.message || "No extra details written."}`;

    const encodedMessage = encodeURIComponent(textPayload);
    
    // Upgraded to direct API to protect the URL encoding hop
    window.open(`https://api.whatsapp.com/send?phone=919016151093&text=${encodedMessage}`, "_blank");

    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="section-padding bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-cyan-500 font-semibold text-sm tracking-wide uppercase mb-4">
            Get In Touch
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Let's Discuss Your Project
          </h2>
          <p className="text-slate-600 text-lg">
            Ready to transform your operations? Contact us for a free consultation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          
          {/* Clickable Left Column Cards */}
          <div className="space-y-4">
            
            {/* 1. Phone / WhatsApp */}
            <a 
              href="https://wa.me/919016151093?text=Hi%20Droniq%20Geo%20Surveys,%20I%20would%20like%20to%20discuss%20a%20project." 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-start gap-4 p-4 -mx-4 rounded-2xl hover:bg-cyan-50/50 transition-all cursor-pointer group block"
            >
              <div className="w-12 h-12 bg-cyan-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-100 group-hover:scale-105 transition-all">
                <Phone className="w-5 h-5 text-cyan-500" />
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Phone / WhatsApp</p>
                <p className="text-lg font-medium text-slate-900 group-hover:text-cyan-600 transition-colors" data-testid="contact-phone">
                  +91-9016151093
                </p>
              </div>
            </a>

            {/* 2. Email */}
            <a 
              href="mailto:droniqsurvey@gmail.com?subject=Website%20Inquiry:%20Project%20Consultation" 
              className="flex items-start gap-4 p-4 -mx-4 rounded-2xl hover:bg-cyan-50/50 transition-all cursor-pointer group block"
            >
              <div className="w-12 h-12 bg-cyan-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-100 group-hover:scale-105 transition-all">
                <Mail className="w-5 h-5 text-cyan-500" />
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Email</p>
                <p className="text-lg font-medium text-slate-900 group-hover:text-cyan-600 transition-colors" data-testid="contact-email">
                  droniqsurvey@gmail.com
                </p>
              </div>
            </a>

            {/* 3. Office */}
            <a 
              href="https://www.google.com/maps/search/?api=1&query=Shop+No.+101,+Dutt+Avalon,+Tragad,+Ahmedabad" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-start gap-4 p-4 -mx-4 rounded-2xl hover:bg-cyan-50/50 transition-all cursor-pointer group block"
            >
              <div className="w-12 h-12 bg-cyan-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-100 group-hover:scale-105 transition-all">
                <MapPin className="w-5 h-5 text-cyan-500" />
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Office</p>
                <address className="text-slate-900 not-italic leading-relaxed group-hover:text-cyan-600 transition-colors" data-testid="contact-address">
                  Shop No. 103, Dutt Avalon<br />
                  Opp: Satyamev Royal 3<br />
                  Nr: Apollo International School<br />
                  Tragad, Ahmedabad - 382470
                </address>
              </div>
            </a>

            {/* 4. Instagram */}
            <a 
              href="https://www.instagram.com/droniq_geo_surveys" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-start gap-4 p-4 -mx-4 rounded-2xl hover:bg-cyan-50/50 transition-all cursor-pointer group block"
            >
              <div className="w-12 h-12 bg-cyan-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-100 group-hover:scale-105 transition-all">
                <Instagram className="w-5 h-5 text-cyan-500" />
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Instagram</p>
                <p className="text-lg font-medium text-slate-900 group-hover:text-cyan-600 transition-colors" data-testid="contact-instagram">
                  @droniq_geo_surveys
                </p>
              </div>
            </a>

          </div>

          {/* Right Column WhatsApp Form */}
          <div className="bg-slate-50 rounded-3xl p-8">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Your Name</label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full h-12 rounded-xl border-slate-200 focus:border-cyan-500 focus:ring-cyan-500 bg-white"
                  placeholder="Enter your name"
                  data-testid="form-name-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full h-12 rounded-xl border-slate-200 focus:border-cyan-500 focus:ring-cyan-500 bg-white"
                    placeholder="your@email.com"
                    data-testid="form-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full h-12 rounded-xl border-slate-200 focus:border-cyan-500 focus:ring-cyan-500 bg-white"
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="form-phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Service Required</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full h-12 rounded-xl border border-slate-200 px-4 focus:border-cyan-500 focus:ring-cyan-500 focus:outline-none bg-white"
                  data-testid="form-service-select"
                >
                  <option value="">Select a service</option>
                  {servicesData.map(service => (
                    <option key={service.id} value={service.id}>{service.title}</option>
                  ))}
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full rounded-xl border-slate-200 focus:border-cyan-500 focus:ring-cyan-500 resize-none bg-white"
                  placeholder="Tell us about your project..."
                  data-testid="form-message-input"
                />
              </div>

              <Button 
                type="submit"
                className="w-full bg-[#25D366] hover:bg-[#20ba5a] text-white font-semibold py-6 rounded-xl text-base flex items-center justify-center gap-2 shadow-md transition-all cursor-pointer"
                data-testid="form-submit-btn"
              >
                Continue to WhatsApp
                <Send className="w-5 h-5 ml-1" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-slate-900 text-white py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-4 gap-12 mb-12">
        {/* Brand */}
        <div className="md:col-span-2">
          <Link to="/" className="flex items-center gap-3 mb-6">
            <img 
              src='/images/logo.png'
              alt="Droniq Logo"
              className="w-10 h-10 object-contain"
            />
            <div>
              <span className="font-heading font-bold text-xl text-white tracking-tight">DRONIQ</span>
              <span className="block text-[10px] font-medium text-slate-400 tracking-widest">GEO SURVEYS</span>
            </div>
          </Link>
          <p className="text-slate-400 leading-relaxed max-w-md">
            Professional drone survey and mapping solutions. Transforming aerial data 
            into actionable insights for businesses across India.
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-semibold text-white mb-4">Quick Links</h4>
          <nav className="space-y-3">
            {['Home', 'About', 'Services', 'Process', 'Contact'].map((link) => (
              <a
                key={link}
                href={`/#${link.toLowerCase()}`}
                className="block text-slate-400 hover:text-cyan-400 transition-colors"
              >
                {link}
              </a>
            ))}
          </nav>
        </div>

        {/* Services */}
        <div>
          <h4 className="font-semibold text-white mb-4">Services</h4>
          <nav className="space-y-3">
            {servicesData.slice(0, 5).map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.id}`}
                className="block text-slate-400 hover:text-cyan-400 transition-colors"
              >
                {service.title}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-slate-500 text-sm">
          © {new Date().getFullYear()} Droniq Geo Surveys. All rights reserved.
        </p>
        <a
          href="https://www.instagram.com/droniq_geo_surveys"
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-400 hover:text-cyan-400 transition-colors"
          data-testid="footer-instagram"
        >
          <Instagram className="w-5 h-5" />
        </a>
      </div>
    </div>
  </footer>
);

// Home Page Component
const HomePage = () => (
  <>
    <HeroSection />
    <StatsSection />
    <AboutSection />
    <ServicesSection />
    <ProcessSection />
    <ContactSection />
  </>
);

// Service Detail Page Component
const ServicePage = () => {
  const location = useLocation();
  const serviceId = location.pathname.split('/services/')[1];
  const service = servicesData.find(s => s.id === serviceId);

  if (!service) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Service Not Found</h1>
          <Link to="/" className="text-cyan-500 hover:underline">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="service-detail-page">
      {/* Page Header */}
      <div className="page-header py-20 text-white relative overflow-hidden">
        <div className="page-header-pattern" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/#services" className="inline-flex items-center text-white/80 hover:text-white mb-6 text-sm">
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
            Back to Services
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
              <service.icon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">{service.title}</h1>
          <p className="text-xl text-white/90 max-w-2xl">{service.fullDesc}</p>
        </div>
      </div>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-8">
                What We Deliver
              </h2>
              <div className="space-y-4">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Service Image */}
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl">
              <img 
                src={service.image || '/images/services/default.jpg'}//src='frontend/public/images'
                alt={`${service.title} - Aerial view`}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center">
            Applications
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {service.applications.map((app, index) => (
              <span
                key={index}
                className="px-6 py-3 bg-white rounded-full text-slate-700 font-medium border border-slate-200 shadow-sm"
              >
                {app}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-teal-500 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Contact us today for a free consultation and quote for your {service.title.toLowerCase()} project.
          </p>
          <Link to="/get-quote">
            <Button className="bg-white text-cyan-600 hover:bg-slate-100 font-medium px-8 py-6 rounded-full text-base">
              Get a Free Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Other Services */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center">
            Other Services
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {servicesData.filter(s => s.id !== service.id).slice(0, 3).map((s) => (
              <Link
                key={s.id}
                to={`/services/${s.id}`}
                className="service-card bg-white border border-slate-200 rounded-2xl p-6 text-center block"
              >
                <div className="w-12 h-12 bg-cyan-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <s.icon className="w-6 h-6 text-cyan-500" />
                </div>
                <h3 className="font-heading font-semibold text-slate-900">{s.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// UPGRADED Get Quote Page Component (WhatsApp Form Dispatcher)
const GetQuotePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedService = servicesData.find(s => s.id === formData.service);
    const serviceName = selectedService ? selectedService.title : (formData.service === 'other' ? 'Other / Custom' : 'Detailed Quote Request');

    const quotePayload = 
`*New Quote Request - Droniq Geo Surveys* \u{1F6F8}

*\u{1F464} Name:* ${formData.name}
*\u{1F4E7} Email:* ${formData.email}
*\u{1F4DE} Phone:* ${formData.phone || "Not provided"}
*\u{1F3AF} Service Required:* ${serviceName}

*\u{1F4DD} Project Specs & Requirements:*
${formData.message || "No specific requirement details written."}`;

    const encodedMessage = encodeURIComponent(quotePayload);
    
    window.open(`https://api.whatsapp.com/send?phone=919016151093&text=${encodedMessage}`, "_blank");

    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="pt-20 min-h-screen bg-slate-50" data-testid="get-quote-page">
      {/* Page Header */}
      <div className="page-header py-16 text-white relative overflow-hidden">
        <div className="page-header-pattern" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">Get a Quote</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Fill out the form below and our team will get back to you within 24 hours.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        
        {/* Interactive Contact Info Bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
            
            {/* Clickable Call / WhatsApp Pill */}
            <a 
              href="https://wa.me/919016151093?text=Hi,%20I%20am%20on%20your%20Quote%20page%20and%20have%20a%20question."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-2 -m-2 rounded-xl hover:bg-cyan-50/50 transition cursor-pointer group"
            >
              <div className="w-10 h-10 bg-cyan-50 rounded-full flex items-center justify-center group-hover:bg-cyan-100 group-hover:scale-105 transition-all">
                <Phone className="w-5 h-5 text-cyan-500" />
              </div>
              <div className="text-left">
                <p className="text-xs text-slate-500">Call / WhatsApp</p>
                <p className="font-semibold text-slate-900 group-hover:text-cyan-600 transition-colors">
                  +91-9016151093
                </p>
              </div>
            </a>

            <div className="hidden md:block w-px h-10 bg-slate-200" />
            
            {/* Clickable Email Pill */}
            <a 
              href="mailto:droniqsurvey@gmail.com?subject=Quote%20Page%20Inquiry"
              className="flex items-center gap-3 p-2 -m-2 rounded-xl hover:bg-cyan-50/50 transition cursor-pointer group"
            >
              <div className="w-10 h-10 bg-cyan-50 rounded-full flex items-center justify-center group-hover:bg-cyan-100 group-hover:scale-105 transition-all">
                <Mail className="w-5 h-5 text-cyan-500" />
              </div>
              <div className="text-left">
                <p className="text-xs text-slate-500">Email Us</p>
                <p className="font-semibold text-slate-900 group-hover:text-cyan-600 transition-colors">
                  droniqsurvey@gmail.com
                </p>
              </div>
            </a>

          </div>
        </div>

        {/* Quote Form */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 md:p-12">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Your Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full h-14 rounded-xl border-slate-200 focus:border-cyan-500 focus:ring-cyan-500 text-lg bg-white"
                placeholder="Enter your name"
                data-testid="quote-name-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full h-14 rounded-xl border-slate-200 focus:border-cyan-500 focus:ring-cyan-500 text-lg bg-white"
                  placeholder="your@email.com"
                  data-testid="quote-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full h-14 rounded-xl border-slate-200 focus:border-cyan-500 focus:ring-cyan-500 text-lg bg-white"
                  placeholder="+91 XXXXX XXXXX"
                  data-testid="quote-phone-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Service Required <span className="text-red-500">*</span>
              </label>
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="w-full h-14 rounded-xl border border-slate-200 px-4 focus:border-cyan-500 focus:ring-cyan-500 focus:outline-none bg-white text-lg"
                data-testid="quote-service-select"
              >
                <option value="">Select a service</option>
                {servicesData.map(service => (
                  <option key={service.id} value={service.id}>{service.title}</option>
                ))}
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Message
              </label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                className="w-full rounded-xl border-slate-200 focus:border-cyan-500 focus:ring-cyan-500 resize-none text-lg bg-white"
                placeholder="Tell us about your project requirements..."
                data-testid="quote-message-input"
              />
            </div>

            <Button 
              type="submit"
              className="w-full bg-[#25D366] hover:bg-[#20ba5a] text-white font-semibold py-7 rounded-xl text-lg flex items-center justify-center gap-2 shadow-md transition-all cursor-pointer"
              data-testid="quote-submit-btn"
            >
              Continue to WhatsApp
              <Send className="w-5 h-5 ml-1" />
            </Button>
          </form>
        </div>

        {/* Upgraded Clickable Google Maps Bottom Link */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          <p>Or visit our office:</p>
          <a 
            href="https://www.google.com/maps/search/?api=1&query=Shop+No.+101,+Dutt+Avalon,+Tragad,+Ahmedabad" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block font-medium text-slate-700 hover:text-cyan-600 transition-colors mt-1"
          >
            Shop No. 103, Dutt Avalon, Opp: Satyamev Royal 3,<br />
            Nr: Apollo International School, Tragad, Ahmedabad - 382470
          </a>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="min-h-screen bg-white">
        <Navigation />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services/:serviceId" element={<ServicePage />} />
            <Route path="/get-quote" element={<GetQuotePage />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;